import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faEye,
  faTimes,
  faFile,
  faFileImage,
  faFileVideo,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faFileText,
  faFileCode,
  faFileArchive,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { getFileIcon } from '../utils/storage';

const FileContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4);
  border: 1px solid var(--border-color-light);
  border-radius: var(--radius-xl);
  background: linear-gradient(135deg, var(--surface-color) 0%, var(--background-color) 100%);
  max-width: 320px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  box-shadow: var(--shadow-sm);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, transparent 0%, var(--primary-50) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: var(--primary-600);
    transform: translateY(-2px) scale(1.02);
    box-shadow: var(--shadow-lg);

    &::before {
      opacity: 0.3;
    }
  }

  &:active {
    transform: translateY(-1px) scale(1.01);
    box-shadow: var(--shadow-md);
  }

  ${props => props.isImage && `
    flex-direction: column;
    padding: var(--space-3);
    max-width: 220px;
  `}

  ${props => props.isVideo && `
    flex-direction: column;
    padding: var(--space-3);
    max-width: 220px;
  `}
`;

const FileIcon = styled.div`
  font-size: var(--text-lg);
  color: var(--text-secondary);
  flex-shrink: 0;
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.div`
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-color);
  margin-bottom: var(--space-1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSize = styled.div`
  font-size: var(--text-xs);
  color: var(--text-secondary);
`;

const ImagePreview = styled.img`
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: var(--radius-md);
  margin-bottom: var(--space-2);
`;

const VideoPreview = styled.video`
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: var(--radius-md);
  margin-bottom: var(--space-2);
`;

const FileActions = styled.div`
  display: flex;
  gap: var(--space-2);
  align-items: center;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: var(--space-2);
  border-radius: var(--radius-lg);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: var(--text-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;

  &:hover {
    background: var(--primary-50);
    color: var(--primary-600);
    transform: scale(1.1);
    box-shadow: var(--shadow-sm);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background: var(--surface-color);
  border-radius: var(--radius-2xl);
  max-width: 90vw;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
  border: 1px solid var(--border-color-light);
`;

const ModalHeader = styled.div`
  padding: var(--space-4);
  border-bottom: 1px solid var(--border-color-light);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h3`
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--text-color);
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: var(--space-2);
  border-radius: var(--radius-lg);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: var(--neutral-100);
    color: var(--text-color);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }
`;

const ModalContent = styled.div`
  padding: var(--space-4);
  max-height: calc(90vh - 80px);
  overflow-y: auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImageModal = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const VideoModal = styled.video`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const DocumentContainer = styled.div`
  text-align: center;
  color: var(--text-secondary);
`;

const DocumentIcon = styled.div`
  font-size: var(--text-6xl);
  margin-bottom: var(--space-4);
`;

const DocumentText = styled.div`
  font-size: var(--text-base);
  margin-bottom: var(--space-4);
`;

const DownloadButton = styled.button`
  padding: var(--space-3) var(--space-5);
  background: linear-gradient(135deg, var(--primary-600), var(--primary-700));
  color: white;
  border: none;
  border-radius: var(--radius-lg);
  cursor: pointer;
  font-size: var(--text-sm);
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: linear-gradient(135deg, var(--primary-700), var(--primary-800));
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(90, 99, 230, 0.3);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid var(--neutral-300);
  border-radius: 50%;
  border-top-color: var(--primary-600);
  animation: spin 1s ease-in-out infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

function FileViewer({ file, onDownload, onView }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleView = async () => {
    if (file.downloadURL) {
      setIsModalOpen(true);
    } else if (onView) {
      setIsLoading(true);
      try {
        await onView(file);
        setIsModalOpen(true);
      } catch (error) {
        console.error('Error viewing file:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDownload = async () => {
    if (file.downloadURL) {
      const link = document.createElement('a');
      link.href = file.downloadURL;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (onDownload) {
      try {
        await onDownload(file);
      } catch (error) {
        console.error('Error downloading file:', error);
      }
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const isImage = file.type?.startsWith('image/');
  const isVideo = file.type?.startsWith('video/');

  return (
    <>
      <FileContainer
        isImage={isImage}
        onClick={handleView}
      >
        {isImage && file.downloadURL && (
          <ImagePreview src={file.downloadURL} alt={file.name} />
        )}

        {isVideo && file.downloadURL && (
          <VideoPreview src={file.downloadURL} />
        )}

        <FileIcon>
          <FontAwesomeIcon icon={getFileIcon(file.type)} />
        </FileIcon>

        <FileInfo>
          <FileName>{file.name}</FileName>
          <FileSize>{formatFileSize(file.size)}</FileSize>
        </FileInfo>

        <FileActions>
          <ActionButton
            onClick={(e) => {
              e.stopPropagation();
              handleView();
            }}
            title="View file"
            disabled={isLoading}
          >
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <FontAwesomeIcon icon={faEye} />
            )}
          </ActionButton>

          <ActionButton
            onClick={(e) => {
              e.stopPropagation();
              handleDownload();
            }}
            title="Download file"
          >
            <FontAwesomeIcon icon={faDownload} />
          </ActionButton>
        </FileActions>
      </FileContainer>

      {/* File Modal */}
      {isModalOpen && (
        <ModalOverlay onClick={() => setIsModalOpen(false)}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{file.name}</ModalTitle>
              <CloseButton onClick={() => setIsModalOpen(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </CloseButton>
            </ModalHeader>

            <ModalContent>
              {isImage && file.downloadURL && (
                <ImageModal src={file.downloadURL} alt={file.name} />
              )}

              {isVideo && file.downloadURL && (
                <VideoModal src={file.downloadURL} controls>
                  <source src={file.downloadURL} type={file.type} />
                  Your browser does not support the video tag.
                </VideoModal>
              )}

              {!file.downloadURL && (
                <DocumentContainer>
                  <DocumentIcon>
                    <FontAwesomeIcon icon={getFileIcon(file.type)} />
                  </DocumentIcon>
                  <DocumentText>
                    This file cannot be previewed directly.
                    <br />
                    Click download to view it.
                  </DocumentText>
                  <DownloadButton onClick={handleDownload}>
                    <FontAwesomeIcon icon={faDownload} />
                    Download File
                  </DownloadButton>
                </DocumentContainer>
              )}
            </ModalContent>
          </ModalContainer>
        </ModalOverlay>
      )}
    </>
  );
}

export default FileViewer;
